import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaLock, FaArrowLeft, FaCheckCircle } from "react-icons/fa";
import { API_BASE_URL } from "../environment";
import { API } from "../common/Constants";
import "../styles/verification.css";

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Get token from URL params
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const email = queryParams.get("email");

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    submitted: false,
    error: false,
    validToken: true,
  });

  // Verify token validity when component loads
  useEffect(() => {
    if (!token || !email) {
      setFormStatus((prev) => ({
        ...prev,
        error: true,
        validToken: false,
        errorMessage:
          "Invalid or missing reset token. Please request a new password reset link.",
      }));
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}${API.VERIFY_RESET_TOKEN}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              token,
              email,
            }),
          }
        );

        if (!response.ok) {
          setFormStatus((prev) => ({
            ...prev,
            error: true,
            validToken: false,
            errorMessage:
              "Invalid or expired reset token. Please request a new password reset link.",
          }));
        }
      } catch (error) {
        console.error("Token verification error:", error);
        setFormStatus((prev) => ({
          ...prev,
          error: true,
          validToken: false,
          errorMessage:
            "Failed to verify reset token. Please request a new password reset link.",
        }));
      }
    };

    verifyToken();
  }, [token, email]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Clear error for this field when user types
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.password) {
      errors.password = "Password is required";
    } else if (formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters long";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormStatus((prev) => ({
      ...prev,
      submitting: true,
      submitted: false,
      error: false,
    }));

    try {
      const response = await fetch(`${API_BASE_URL}${API.RESET_PASSWORD}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          email,
          password: formData.password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Password reset failed");
      }

      setFormStatus((prev) => ({
        ...prev,
        submitting: false,
        submitted: true,
        error: false,
      }));

      // Redirect to login page after a short delay
      setTimeout(() => {
        navigate("/login", {
          state: { passwordReset: true },
        });
      }, 3000);
    } catch (error) {
      console.error("Password reset error:", error);
      setFormStatus((prev) => ({
        ...prev,
        submitting: false,
        error: true,
        errorMessage:
          error.message || "Password reset failed. Please try again.",
      }));
    }
  };

  if (!formStatus.validToken) {
    return (
      <section className="login-page section-padding">
        <div className="container">
          <div className="section-header">
            <h2>Reset Password</h2>
            <p>Invalid or Expired Link</p>
          </div>

          <div className="login-content">
            <div className="login-form-container">
              <div className="error-alert">
                <p>{formStatus.errorMessage}</p>
              </div>
              <div className="text-center mt-4">
                <Link to="/forgot-password" className="btn btn-primary">
                  Request New Reset Link
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="login-page section-padding">
      <div className="container">
        <div className="section-header">
          <h2>Reset Password</h2>
          <p>Create a new password for your account</p>
        </div>

        <div className="login-content">
          <div className="login-form-container">
            <Link to="/login" className="back-link">
              <FaArrowLeft /> Back to Login
            </Link>

            {formStatus.submitted ? (
              <div className="success-message">
                <FaCheckCircle className="success-icon" />
                <h3>Password Reset Successful!</h3>
                <p>
                  Your password has been successfully reset. You will be
                  redirected to the login page shortly.
                </p>
              </div>
            ) : (
              <form className="login-form" onSubmit={handleSubmit}>
                {formStatus.error && (
                  <div className="error-alert">
                    <p>
                      {formStatus.errorMessage ||
                        "Password reset failed. Please try again."}
                    </p>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="password">
                    <FaLock /> New Password
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className={formErrors.password ? "error" : ""}
                  />
                  {formErrors.password && (
                    <span className="error-message">{formErrors.password}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">
                    <FaLock /> Confirm New Password
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={formErrors.confirmPassword ? "error" : ""}
                  />
                  {formErrors.confirmPassword && (
                    <span className="error-message">
                      {formErrors.confirmPassword}
                    </span>
                  )}
                </div>

                <button
                  type="submit"
                  className="btn btn-primary login-btn"
                  disabled={formStatus.submitting}
                >
                  {formStatus.submitting
                    ? "Resetting Password..."
                    : "Reset Password"}
                </button>
              </form>
            )}
          </div>

          <div className="login-info">
            <div className="login-info-content">
              <h3>Password Security</h3>
              <div className="login-info-icon">
                <FaLock />
              </div>
              <p className="login-info-description">
                Create a strong password to protect your account. A strong
                password helps prevent unauthorized access to your account.
              </p>
              <ul className="login-benefits">
                <li>
                  <span className="benefit-dot"></span>
                  Use at least 8 characters
                </li>
                <li>
                  <span className="benefit-dot"></span>
                  Include uppercase and lowercase letters
                </li>
                <li>
                  <span className="benefit-dot"></span>
                  Add numbers and special characters
                </li>
                <li>
                  <span className="benefit-dot"></span>
                  Avoid using common words or personal info
                </li>
              </ul>
              <div className="login-info-footer">
                <p>Need assistance?</p>
                <Link to="/#contact" className="support-link">
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResetPassword;
